import { useAuth, useCancelSubscriptionsOnDomainChange, UserRole } from "@/features/auth";
import { getEnv, isNotProduction } from "@/shared/utils";
import { setUserSubscriptionsContext } from "@/shared/utils/datadog-logger";
import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { useInvalidateQueriesOnConnect } from "../hooks/useInvalidateQueriesOnConnect";
import {
    createRSocketConnectionManager,
    createTopicSubscriptionService,
    RSocketConnectionManager,
    RSocketTopicSubscriptionService,
} from "../service";
import { ConnectionStatus, RSocketProviderProps, RSocketState, TopicMessageHandler, TopicSubscription } from "../types";
import { normalizeError } from "../utils/errorHandling";
import { RSocketContext } from "./RSocketContext";

const WebSocketTerminal = isNotProduction() ? lazy(() => import("../components/WebSocketTerminal")) : null;
const isDebug = isNotProduction();

/**
 * RSocket provider component
 * @param props Component props
 * @returns RSocket provider component
 */
export function RSocketProvider({ children }: Readonly<RSocketProviderProps>) {
    const { getAccessToken, apiDomain, previewRole, hasRole } = useAuth();
    const [status, setStatus] = useState<ConnectionStatus>(ConnectionStatus.DISCONNECTED);
    const [lastError, setLastError] = useState<Error | null>(null);
    const [messagesReceived, setMessagesReceived] = useState<number>(0);
    const [currentSubscriptions, setCurrentSubscriptionsState] = useState<string[]>([]);
    const [connectionManager, setConnectionManager] = useState<RSocketConnectionManager | null>(null);
    const [subscriptionService, setSubscriptionService] = useState<RSocketTopicSubscriptionService | null>(null);
    const url = getEnv("VITE_SOCKET_URL");
    const isConnected = status === ConnectionStatus.CONNECTED;

    // Refetch cached data when WebSocket connects to avoid stale state from the connection window
    useInvalidateQueriesOnConnect(status);

    // cancel all subscriptions on apiDomain change to prevent handling the same event multiple times
    const cancelQueries = useCallback(() => subscriptionService && subscriptionService.cancelAllSubscriptions(), [subscriptionService]);
    useCancelSubscriptionsOnDomainChange(apiDomain, previewRole, cancelQueries);

    useEffect(() => {
        console.debug(`Initializing RSocket with URL: ${url}`);

        const manager = createRSocketConnectionManager(url, {
            onStatusChange: (newStatus) => {
                console.debug(`RSocket status changed: ${newStatus}`);
                setStatus(newStatus);
            },
            onError: (error) => {
                console.error("RSocket error in context:", error);
                setLastError(error);
            },
            maxReconnectAttempts: 10,
            reconnectDelay: 1000,
        });

        // Only track message counts in debug mode to avoid unnecessary re-renders
        const onMessageCountUpdate = isDebug ? (count: number) => setMessagesReceived(count) : undefined;
        const onSubscriptionsChange = (topics: string[]) => setCurrentSubscriptionsState(topics);

        const service = createTopicSubscriptionService(manager, onMessageCountUpdate, onSubscriptionsChange);

        setConnectionManager(manager);
        setSubscriptionService(service);

        return () => {
            if (service) {
                service.cancelAllSubscriptions();
            }

            if (manager?.isConnected()) {
                manager.disconnect();
            }
        };
    }, [url]);

    const connect = useCallback(async () => {
        if (!connectionManager) {
            console.warn("Cannot connect - RSocket connection manager not initialized");
            return;
        }

        try {
            console.debug("Manually connecting to RSocket server");
            await connectionManager.connect();
        } catch (err) {
            const error = normalizeError(err);
            console.error("Manual connection failed:", error);
            setLastError(error);
            throw error;
        }
    }, [connectionManager]);

    const disconnect = useCallback(() => {
        if (!connectionManager) {
            console.warn("Cannot disconnect - RSocket connection manager not initialized");
            return;
        }

        console.debug("Manually disconnecting from RSocket server");

        if (subscriptionService) {
            subscriptionService.cancelAllSubscriptions();
        }

        connectionManager.disconnect();
    }, [connectionManager, subscriptionService]);

    const subscribeTopic = useCallback(
        async <T,>(topicName: string, onMessage: TopicMessageHandler<T>): Promise<TopicSubscription> => {
            if (!subscriptionService) {
                throw new Error("RSocket subscription service not initialized");
            }

            const accessToken = await getAccessToken();
            console.debug(`Subscribing to RSocket topic: ${topicName}`);
            return subscriptionService.subscribe<T>(topicName, onMessage, accessToken);
        },
        [subscriptionService, getAccessToken]
    );

    // Auto-connect when the connection manager is initialized
    useEffect(() => {
        if (connectionManager && !isConnected) {
            console.debug("Auto-connecting to RSocket server");
            connect().catch((error) => {
                console.error("Auto-connect failed:", error);
            });
        }
    }, [connectionManager, isConnected, connect]);

    // Update Datadog with current subscriptions
    useEffect(() => {
        setUserSubscriptionsContext(currentSubscriptions);
    }, [currentSubscriptions]);

    const value: RSocketState = useMemo(
        () => ({
            isConnected,
            status,
            lastError,
            messagesReceived,
            connect,
            disconnect,
            subscribeTopic,
            getCurrentSubscriptions: () => currentSubscriptions,
        }),
        [isConnected, status, lastError, messagesReceived, connect, disconnect, subscribeTopic, currentSubscriptions]
    );

    return (
        <RSocketContext value={value}>
            <div className="flex h-dvh flex-col">
                <div className="flex flex-col overflow-auto">{children}</div>
                {/* Hidden markers so e2e tests can wait on the RSocket lifecycle and per-topic subscriptions. */}
                <span data-testid="rsocket-connection-status" data-status={status} hidden aria-hidden="true" />
                {currentSubscriptions.map((topic) => (
                    <span key={topic} data-testid="rsocket-subscription" data-topic={topic} hidden aria-hidden="true" />
                ))}
                {WebSocketTerminal && hasRole(UserRole.ADMIN, true) && (
                    <Suspense fallback={null}>
                        <WebSocketTerminal />
                    </Suspense>
                )}
            </div>
        </RSocketContext>
    );
}
